import { useEffect, useState } from "react";
import { Package, Trash2, PlusCircle, Save, X, Layers } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";

export default function PurchaseForm({ onSaved, onCancel }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [details, setDetails] = useState([]);

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data?.data || res.data || []);
    } catch {
      setSuppliers([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data?.data || res.data || []);
    } catch {
      setProducts([]);
    }
  };

  /* =========================
     LINE ITEMS
  ========================== */
  const addDetail = () => {
    setDetails([
      ...details,
      {
        product_id: "",
        presentation_id: null,
        cantidad: 1,
        precio_unitario: 0,
        productData: null,
      },
    ]);
  };

  const updateDetail = (index, field, value) => {
    const updated = [...details];
    updated[index][field] = value;

    if (field === "product_id") {
      const selectedProduct = products.find((p) => p.id == value);
      updated[index].productData = selectedProduct;
      updated[index].presentation_id = null;
    }

    setDetails(updated);
  };

  const removeDetail = (index) => {
    setDetails(details.filter((_, i) => i !== index));
  };

  /* =========================
     PRESENTATION HELPERS
  ========================== */
  const hasVariantPresentations = (detail) => {
    if (!detail.productData?.presentations) return false;
    return detail.productData.presentations.some((p) => p.tipo_presentacion === "variante");
  };

  const getPresentationName = (detail) => {
    if (!detail.presentation_id || !detail.productData?.presentations) {
      return "";
    }
    const pres = detail.productData.presentations.find((p) => p.id == detail.presentation_id);
    return pres ? pres.descripcion : "";
  };

  /* =========================
     LINE SUBTOTAL CALCULATION
  ========================== */
  const calculateLineSubtotal = (detail) => {
    const unitPrice = Number(detail.precio_unitario || 0);
    const quantity = Number(detail.cantidad || 0);
    return unitPrice * quantity;
  };

  /* =========================
     TOTALS (net cost, no tax)
  ========================== */
  const totalAmount = details.reduce((sum, d) => sum + calculateLineSubtotal(d), 0);

  /* =========================
     ACTIONS
  ========================== */
  const resetForm = () => {
    setSupplierId("");
    setPurchaseDate("");
    setInvoiceNumber("");
    setDetails([]);
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierId || !purchaseDate || !invoiceNumber || details.length === 0) {
      Swal.fire("Error", "Please fill out all fields", "error");
      return;
    }

    for (let i = 0; i < details.length; i++) {
      const detail = details[i];
      if (hasVariantPresentations(detail) && !detail.presentation_id) {
        Swal.fire(
          "Error",
          `You must select a variant for ${detail.productData.nombre}`,
          "error"
        );
        return;
      }
    }

    const payload = {
      supplier_id: supplierId,
      fecha_compra: purchaseDate,
      numero_factura_proveedor: invoiceNumber,
      details: details.map((d) => ({
        product_id: d.product_id,
        presentation_id: d.presentation_id || null,
        cantidad: Number(d.cantidad),
        precio_unitario: Number(d.precio_unitario),
      })),
    };

    try {
      await api.post("/purchases", payload);
      Swal.fire("Saved", "Purchase registered successfully", "success");
      resetForm();
      onSaved?.();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Error saving the purchase", "error");
    }
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Package size={24} color="#667eea" />
          <h2 className="card-title">New Purchase</h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
            className="mb-4"
          >
            <div className="form-group">
              <label className="form-label">Supplier *</label>
              <select className="form-control" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} required>
                <option value="">Select a supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Purchase Date *</label>
              <input
                type="date"
                className="form-control"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Supplier Invoice # *</label>
              <input
                type="text"
                className="form-control"
                placeholder="E.g.: INV-001-2026"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                required
              />
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 style={{ fontSize: "16px", fontWeight: 600, margin: 0 }}>Products</h4>
              <button type="button" className="btn btn-primary btn-sm" onClick={addDetail}>
                <PlusCircle size={16} />
                Add Product
              </button>
            </div>

            {details.length === 0 ? (
              <div className="alert alert-info text-center">
                No products added yet. Click "Add Product" to get started.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {details.map((d, index) => (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: hasVariantPresentations(d)
                        ? "2fr 1.5fr 1fr 1fr 1fr 60px"
                        : "2fr 1fr 1fr 1fr 60px",
                      gap: "12px",
                      padding: "12px",
                      background: "#f7fafc",
                      borderRadius: "8px",
                      alignItems: "end",
                    }}
                  >
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Product *
                      </label>
                      <select
                        className="form-control"
                        value={d.product_id}
                        onChange={(e) => updateDetail(index, "product_id", e.target.value)}
                        required
                      >
                        <option value="">Select</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nombre}
                            {p.presentations?.some((pr) => pr.tipo_presentacion === "variante") && " 🎨"}
                            {p.presentations?.some((pr) => pr.tipo_presentacion === "empaque") && " 📦"}
                          </option>
                        ))}
                      </select>
                    </div>

                    {hasVariantPresentations(d) && (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label
                          className="form-label"
                          style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <Layers size={12} />
                          Variant *
                        </label>
                        <select
                          className="form-control"
                          value={d.presentation_id || ""}
                          onChange={(e) => updateDetail(index, "presentation_id", e.target.value)}
                          required
                          style={{ borderColor: "#805ad5" }}
                        >
                          <option value="">Select variant</option>
                          {d.productData.presentations
                            .filter((pres) => pres.tipo_presentacion === "variante")
                            .map((pres) => (
                              <option key={pres.id} value={pres.id}>
                                {pres.descripcion}
                                {` (Stock: ${pres.stock_actual || 0})`}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Quantity *
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        value={d.cantidad}
                        onChange={(e) => updateDetail(index, "cantidad", e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Unit Cost *
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={d.precio_unitario}
                        onChange={(e) => updateDetail(index, "precio_unitario", e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: "12px" }}>
                        Subtotal
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={`$${calculateLineSubtotal(d).toFixed(2)}`}
                        disabled
                        style={{
                          background: "#e2e8f0",
                          fontWeight: 600,
                          color: "#2d3748",
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => removeDetail(index)}
                      style={{ height: "46px" }}
                      title="Remove product"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          <div
            style={{
              background: "#f7fafc",
              padding: "20px",
              borderRadius: "10px",
            }}
            className="mb-4"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                maxWidth: "400px",
                marginLeft: "auto",
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "18px", fontWeight: 700 }}>Total Purchase:</span>
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#48bb78",
                  }}
                >
                  ${totalAmount.toFixed(2)}
                </span>
              </div>
            </div>

            <small
              style={{
                color: "#718096",
                display: "block",
                marginTop: "16px",
                fontStyle: "italic",
              }}
            >
              * Enter the net cost as shown on the supplier's invoice
            </small>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              Save Purchase
            </button>

            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              <X size={18} />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}